import { Check } from "lucide-react";
import { YMark } from "@/components/Brand";

/**
 * OnboardingShell — 1:1 web port of iOS `src/components/ui/onboarding-shell.tsx`.
 *
 *   • Top nav: Y mark (30px) on the left, 4 numbered/checked circular
 *     step dots on the right (26px each, brand-purple when active/done).
 *   • Eyebrow "STEP N OF 4" (11px 700, 2px letter-spacing, muted).
 *   • 32px Plus Jakarta Sans ExtraBold title with -0.8 letter-spacing.
 *   • Optional subtitle (16px, 23 line-height, secondary text).
 *   • Body area (24px horizontal padding, 24px top margin).
 */

export const ONBOARDING_ORDER = [
  { key: "basics", label: "Profile", path: "/onboarding/basics" },
  { key: "photo", label: "Photo", path: "/onboarding/photo" },
  { key: "interests", label: "Interests", path: "/onboarding/interests" },
  { key: "people", label: "People", path: "/onboarding/people" },
];

export default function OnboardingShell({ step, title, subtitle, children, testId }) {
  const currentIdx = ONBOARDING_ORDER.findIndex((s) => s.key === step);

  return (
    <div
      className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col"
      data-testid={testId}
    >
      <div className="flex-1 overflow-y-auto px-6 pt-3 pb-12">
        <div className="mx-auto w-full max-w-[520px]">
          {/* Top nav row — Y left, dots right */}
          <div className="h-12 flex items-center justify-between">
            <YMark height={30} testId={`${testId}-y-mark`} />
            <div className="flex items-center gap-2">
              {ONBOARDING_ORDER.map((s, i) => {
                const done = i < currentIdx;
                const active = i === currentIdx;
                return (
                  <div
                    key={s.key}
                    className={`w-[26px] h-[26px] rounded-full border-[1.5px] flex items-center justify-center transition-colors ${
                      active
                        ? "border-[#7638FA] bg-[#7638FA] text-white"
                        : done
                        ? "border-[#7638FA] bg-[rgba(118,56,250,0.14)] text-[#7638FA]"
                        : "border-border text-muted-foreground"
                    }`}
                    data-testid={`onboarding-step-${s.key}`}
                  >
                    {done ? (
                      <Check size={12} strokeWidth={3} />
                    ) : (
                      <span className="text-[11px] font-bold">{i + 1}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Eyebrow */}
          <p
            className="text-muted-foreground uppercase font-bold mt-5"
            style={{ fontSize: 11, letterSpacing: "2px" }}
          >
            STEP {currentIdx + 1} OF {ONBOARDING_ORDER.length}
          </p>

          {/* Title (Plus Jakarta Sans ExtraBold, 32px, -0.8 tracking) */}
          <h1
            className="font-jakarta font-extrabold text-foreground mt-2.5"
            style={{ fontSize: 32, letterSpacing: "-0.8px", lineHeight: "38px" }}
            data-testid={`${testId}-title`}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              className="text-muted-foreground mt-2.5"
              style={{ fontSize: 16, lineHeight: "23px" }}
              data-testid={`${testId}-subtitle`}
            >
              {subtitle}
            </p>
          )}

          {/* Body */}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}
